import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Table, Typography } from "antd";
import { useHistory } from "react-router-dom";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import Header from "../../../Components/Header/Header";
import ButtonActions from "../../../Components/Buttons/ButtonActions/ButtonActions";
import { R_ADD_EVENTS, R_EDIT_EVENTS } from "../../../constants/routes";
import classes from "./Events.module.scss";
import { getEventColumns } from "./columns";
import RankingEventModal from "./RankingEventModal/RankingEventModal";
import StoreEventModal from "./StoreEventModal/StoreEventModal";
import { useEventActions } from "./useEventActions";

const { Title, Paragraph } = Typography;

const Events = () => {
  const history = useHistory();
  const {
    getEvents,
    deleteEvent,
    eventsList: events,
    isLoadingEvents,
    deletingEventId,
  } = useEventActions();
  const [selectedRankingEvent, setSelectedRankingEvent] = useState(null);
  const [selectedStoreEvent, setSelectedStoreEvent] = useState(null);
  const [selectedDeleteEvent, setSelectedDeleteEvent] = useState(null);

  useEffect(() => {
    getEvents();
  }, [getEvents]);

  const handleConfirmDelete = async () => {
    const isDeleted = await deleteEvent(selectedDeleteEvent);

    if (isDeleted) {
      setSelectedDeleteEvent(null);
    }
  };

  const handleViewEvent = useCallback((record) => {
    if (record.category === "Ranking") {
      setSelectedRankingEvent(record);
    }

    if (record.category === "Lojinha") {
      setSelectedStoreEvent(record);
    }
  }, []);

  const handleEditEvent = useCallback(
    (record) =>
      history.push({
        pathname: R_EDIT_EVENTS(record.id),
        state: { event: record },
      }),
    [history]
  );

  const handleDeleteEvent = useCallback((record) => {
    setSelectedDeleteEvent(record);
  }, []);

  const columns = useMemo(
    () =>
      getEventColumns({
        events,
        deletingEventId,
        onDeleteEvent: handleDeleteEvent,
        onEditEvent: handleEditEvent,
        onViewEvent: handleViewEvent,
      }),
    [
      deletingEventId,
      events,
      handleDeleteEvent,
      handleEditEvent,
      handleViewEvent,
    ]
  );

  return (
    <div className={classes.EventsPage}>
      <div className={classes.PageHeader}>
        <Header text="Eventos Afinando" />

        <ButtonActions
          variant="primary"
          className={classes.NewEventButton}
          onClick={() => history.push(R_ADD_EVENTS)}
          text="Novo Evento"
          icon={<PlusOutlined />}
        />
      </div>

      <div className={classes.TableArea}>
        <Table
          className={classes.EventsTable}
          columns={columns}
          dataSource={events}
          loading={isLoadingEvents}
          pagination={{
            pageSize: 6,
            position: ["bottomRight"],
            showSizeChanger: false,
          }}
          rowKey="id"
          tableLayout="fixed"
        />
      </div>

      <RankingEventModal
        event={selectedRankingEvent}
        open={Boolean(selectedRankingEvent)}
        onClose={() => setSelectedRankingEvent(null)}
      />

      <StoreEventModal
        event={selectedStoreEvent}
        open={Boolean(selectedStoreEvent)}
        onClose={() => setSelectedStoreEvent(null)}
      />

      <Modal
        width={1000}
        height={407}
        open={Boolean(selectedDeleteEvent)}
        className={classes.DeleteEventModal}
        onCancel={() => setSelectedDeleteEvent(null)}
        footer={
          <div className={classes.DeleteEventModalFooter}>
            <ButtonActions
              variant="outline"
              onClick={() => setSelectedDeleteEvent(null)}
              text="Cancelar"
            />

            <ButtonActions
              variant="danger"
              loading={deletingEventId === selectedDeleteEvent?.id}
              text="Confirmar exclusão"
              onClick={handleConfirmDelete}
            />
          </div>
        }
        closeIcon={<CloseOutlined className={classes.DeleteEventCloseButton} />}
      >
        <Title level={4} className={classes.DeleteEventModalTitle}>
          Confirmar exclusão
        </Title>
        <Paragraph className={classes.DeleteEventModalParagraph}>
          Deseja excluir o Evento{" "}
          <strong style={{ fontWeight: "700" }}>
            {selectedDeleteEvent?.name}
          </strong>{" "}
          ? <br />
          Após a exclusão, ele não poderá ser recuperado.
        </Paragraph>
      </Modal>
    </div>
  );
};

export default Events;
